import api from '@/api-client/client';
import DataTableProvider from '@/components/common/DataTableProvider';
import TextField from '@/components/ui/TextField';
import { useEffect, useState } from 'react';
import { ProviderInterface } from '@/api-client/providers';
import useStore from '@/hooks/useStore';
import {
  IconArchive,
  IconCloseCircle,
  IconEdit,
  IconTrash
} from '@/components/icons';
import Card, { CardBody } from '@/components/ui/Card';
import IconButton from '@/components/ui/IconButton';
import Modal from '@/components/ui/Modal';
import {
  Button,
  Drawer,
  DrawerStage,
  PlusCircleIcon,
  useDrawerContext
} from 'stubben-edge-unified-frontend-react-components';
import { PROVIDERS_DRAWER } from '@/constants/drawers';
import { useForm } from 'react-form-reducer';
import FileField from '@/components/ui/FileField';
import { isAxiosError } from 'axios';

interface FormInterface {
  name: string;
  claims_email?: string;
  claims_telephone?: string;
  support_email?: string;
  support_telephone?: string;
  complaints_email?: string;
  nominal_code?: string;
  logo?: File | null;
}

function Providers() {
  const { toast, authStore } = useStore();
  const drawer = useDrawerContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [outdatedTimestamp, setOutdatedTimestamp] = useState<number>(
    Date.now()
  );
  const [selected, setSelected] = useState<ProviderInterface[]>([]);
  const [rowToDelete, setRowToDelete] = useState<ProviderInterface>();
  const [rowData, setRowData] = useState<ProviderInterface | undefined>();
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { hasPermission } = authStore;

  const { fields, setFields, setField, errors } = useForm<FormInterface>({
    name: '',
    claims_email: '',
    claims_telephone: '',
    support_email: '',
    support_telephone: '',
    complaints_email: '',
    nominal_code: '',
    logo: null
  });

  const handleDeleteAction = (items: Array<ProviderInterface>) => {
    setIsLoading(true);

    Promise.all(
      items.map((item) => {
        return api.providers.deleteProvider(item.uuid);
      })
    )
      .then(() => {
        selected && selected.length > 1
          ? toast.success('Providers deleted.')
          : toast.success('Provider deleted.');
      })
      .catch((err) => {
        if (isAxiosError(err)) {
          selected && selected.length > 1
            ? toast.error('Failed to delete providers.')
            : toast.error('Failed to delete provider.');
        }
      })
      .finally(() => {
        setIsLoading(false);
        setSelected([]);
        setOutdatedTimestamp(Date.now());
        setIsModalOpen(false);
      });
  };

  const handleSubmit = () => {
    setIsLoading(true);

    if (isEditMode && rowData) {
      api.providers
        .update(rowData.uuid, fields)
        .then(() => {
          toast.success('Provider updated.');
          setOutdatedTimestamp(Date.now());
          handleClose();
        })
        .catch((err) => {
          if (isAxiosError(err)) {
            toast.error('Failed to update provider.');
            errors.set(err.response?.data.details);
          }
        })
        .finally(() => setIsLoading(false));
    } else {
      api.providers
        .store(fields)
        .then(() => {
          toast.success('Provider created.');
          setOutdatedTimestamp(Date.now());
          handleClose();
        })
        .catch((err) => {
          if (isAxiosError(err)) {
            toast.error('Failed to create provider.');
            errors.set(err.response?.data.details);
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  const handleClose = () => {
    drawer.close(PROVIDERS_DRAWER);
    setRowData(undefined);
    setIsEditMode(false);
    errors.forget();
    setFields({
      name: '',
      claims_email: '',
      claims_telephone: '',
      support_email: '',
      support_telephone: '',
      complaints_email: '',
      nominal_code: ''
    });
  };

  useEffect(() => {
    if (rowData) {
      setFields({
        name: rowData.name,
        claims_email: rowData.claims_email || '',
        claims_telephone: rowData.claims_telephone || '',
        support_email: rowData.support_email || '',
        support_telephone: rowData.support_telephone || '',
        complaints_email: rowData.complaints_email || '',
        nominal_code: rowData.nominal_code || ''
      });
    }
  }, [rowData]);

  return (
    <div data-testid="providers-page">
      <DataTableProvider<ProviderInterface>
        tableId="providers"
        fetchResolver={async ({ searchTerm, ...listData }) => {
          return await api.providers.list(listData, searchTerm);
        }}
        columns={[
          {
            name: 'Name',
            selector: (row) => row.name,
            align: 'left'
          },
          {
            name: 'Nominal code',
            selector: (row) => row.nominal_code || '-',
            align: 'center'
          }
        ]}
        outdatedTimestamp={outdatedTimestamp}
        actions={(row) => (
          <>
            <IconButton
              size="small"
              onClick={() => {
                setRowToDelete(row);
              }}
              disabled={!hasPermission(['provider_manage'])}
              data-testid={`providers-row-delete-${row.uuid}`}
            >
              <IconTrash />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                setRowData(row);
                setIsEditMode(true);
                drawer.open(PROVIDERS_DRAWER);
              }}
              disabled={!hasPermission(['provider_manage'])}
              data-testid={`providers-row-edit-${row.uuid}`}
            >
              <IconEdit />
            </IconButton>
          </>
        )}
        bulkActions={[
          {
            label: 'Delete provider(s)',
            action: async (selected) => {
              setSelected(selected);
              setIsModalOpen(true);
              return false;
            }
          }
        ]}
        extraActions={
          <Button
            color="primary"
            className="!font-bold"
            variant="text"
            leftSlot={<PlusCircleIcon />}
            onClick={() => {
              drawer.open(PROVIDERS_DRAWER);
            }}
            disabled={!hasPermission(['provider_manage'])}
            testIdentifier="providers-add-provider"
          >
            ADD PROVIDER
          </Button>
        }
        appendAfterRow={(row) => {
          if (rowToDelete && rowToDelete.id === row.id) {
            return (
              <Card
                raised={false}
                bgColor="dimmer"
                className="ml-auto flex w-max items-center space-x-2 p-2"
              >
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-warning-100 text-warning">
                  <IconArchive className="h-5 w-5" />
                </span>
                <span className="text-sm font-semibold">
                  Are you sure you want to delete this provider?
                </span>
                <Button
                  size="small"
                  leftSlot={<IconCloseCircle />}
                  variant="text"
                  onClick={() => {
                    setRowToDelete(undefined);
                  }}
                  testIdentifier={`providers-inline-delete-cancel-${row.uuid}`}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  color="warning"
                  onClick={() => {
                    handleDeleteAction([rowToDelete]);
                  }}
                  loading={isLoading}
                  testIdentifier={`providers-inline-delete-confirm-${row.uuid}`}
                >
                  Remove
                </Button>
              </Card>
            );
          } else {
            return undefined;
          }
        }}
        searchable={true}
        searchPlaceholder="Search providers"
        noResultsText="No providers found"
      />

      <Drawer name={PROVIDERS_DRAWER}>
        <DrawerStage
          title={isEditMode ? 'Edit provider' : 'Add provider'}
          leftSlot={
            isEditMode ? (
              <IconEdit size={32} className="text-gray-500" />
            ) : (
              <PlusCircleIcon size={32} className="text-gray-500" />
            )
          }
          actions={
            <div className="flex items-center justify-between">
              <Button
                variant="text"
                leftSlot={<IconCloseCircle />}
                onClick={handleClose}
                testIdentifier="providers-drawer-cancel"
              >
                Cancel
              </Button>
              <Button
                color="primary"
                onClick={handleSubmit}
                loading={isLoading}
                testIdentifier="providers-drawer-save"
              >
                Save
              </Button>
            </div>
          }
        >
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <TextField
              label="Name*"
              placeholder="Enter name"
              value={fields.name}
              onChange={(value) => setField('name', value)}
              className="lg:col-span-2"
              error={errors.has('name')}
              helperText={errors.get('name')}
              inputProps={{ 'data-testid': 'providers-field-name' }}
            />
            <TextField
              label="Claims email"
              placeholder="Enter email"
              value={fields.claims_email || ''}
              onChange={(value) => setField('claims_email', value)}
              error={errors.has('claims_email')}
              helperText={errors.get('claims_email')}
              inputProps={{ 'data-testid': 'providers-field-claims-email' }}
            />
            <TextField
              label="Claims telephone"
              placeholder="Enter telephone"
              value={fields.claims_telephone || ''}
              onChange={(value) => setField('claims_telephone', value)}
              error={errors.has('claims_telephone')}
              helperText={errors.get('claims_telephone')}
              inputProps={{ 'data-testid': 'providers-field-claims-telephone' }}
            />
            <TextField
              label="Support email"
              placeholder="Enter email"
              value={fields.support_email || ''}
              onChange={(value) => setField('support_email', value)}
              error={errors.has('support_email')}
              helperText={errors.get('support_email')}
              inputProps={{ 'data-testid': 'providers-field-support-email' }}
            />
            <TextField
              label="Support telephone"
              placeholder="Enter telephone"
              value={fields.support_telephone || ''}
              onChange={(value) => setField('support_telephone', value)}
              error={errors.has('support_telephone')}
              helperText={errors.get('support_telephone')}
              inputProps={{ 'data-testid': 'providers-field-support-telephone' }}
            />
            <TextField
              label="Complaints email"
              placeholder="Enter email"
              value={fields.complaints_email || ''}
              onChange={(value) => setField('complaints_email', value)}
              className="lg:col-span-2"
              error={errors.has('complaints_email')}
              helperText={errors.get('complaints_email')}
              inputProps={{ 'data-testid': 'providers-field-complaints-email' }}
            />
            <TextField
              label="Nominal code"
              placeholder="Enter nominal code"
              value={fields.nominal_code || ''}
              onChange={(value) => setField('nominal_code', value)}
              className="lg:col-span-2"
              error={errors.has('nominal_code')}
              helperText={errors.get('nominal_code')}
              inputProps={{ 'data-testid': 'providers-field-nominal-code' }}
            />
            <FileField
              file={fields.logo}
              label="Upload logo"
              type="image"
              onChange={(file) => {
                if (file) {
                  const blob = new Blob([file], { type: file?.type });
                  setField('logo', blob);
                } else {
                  setField('logo', '');
                }
              }}
              inputProps={{
                accept: 'image/*',
                'data-testid': 'providers-field-logo'
              }}
              className="lg:col-span-2"
              error={errors.has('logo')}
              helperText={errors.get('logo')}
            />
          </div>
        </DrawerStage>
      </Drawer>

      <Modal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        cardProps={{ 'data-testid': 'providers-delete-modal' }}
        title={
          selected && selected.length > 1
            ? 'Delete providers'
            : 'Delete provider'
        }
        actions={
          <div className="flex w-full flex-nowrap justify-between space-x-2">
            <Button
              variant="text"
              leftSlot={<IconCloseCircle />}
              onClick={() => setIsModalOpen(false)}
              testIdentifier="providers-delete-modal-cancel"
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={() => {
                handleDeleteAction(selected);
              }}
              loading={isLoading}
              testIdentifier="providers-delete-modal-confirm"
            >
              {selected && selected.length > 1
                ? 'Yes, delete these'
                : 'Yes, delete provider'}
            </Button>
          </div>
        }
      >
        {selected && (
          <p>
            {selected.length > 1
              ? `Are you sure that you want to delete these ${selected?.length} providers?`
              : `Are you sure that you want to delete provider ${selected[0]?.name}?`}
          </p>
        )}
      </Modal>
    </div>
  );
}

export default Providers;
